// Angular
import { Observable, debounceTime, forkJoin, share, takeUntil } from 'rxjs';
import { Component, EventEmitter, HostBinding, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

// Libraries

import { MatDialog } from '@angular/material/dialog';
import { GridsterItem } from 'angular-gridster2';
import { Select } from '@ngxs/store';

// Models
import { AppCleanup } from '../../../../shared/models';
import { Job, JobData, UserUnitPeriodChannelValue } from '../../models';
import { JobUpdate } from '../../models/job.model';
import { UserUnitPeriodChannelDto } from '../../../channel/models/user-unit-period-channel-dto';
import { CalibrationCloseDto } from '../../models/calibration-close-dto';
import { ChartState } from '../job-chart/job-chart.component';

// Services
import { JobService } from '../../services/job.service';
import { CoreService, DCGService } from '../../../../shared/services';
import { DashboardService } from '../dashboard/services/dashboard.service';
import { TimezoneService } from '../../services/timezone.service';
import { SubheaderService } from '../../../../shared/components/subheader/services/subheader.service';
import { JobOverviewService } from '../../services/job-overview.service';
import { SpinnerService } from '../../../../shared/components/spinner/services/spinner.service';
import { AuthService } from '../../../auth';

// Components
import { OffsetChannelDialogComponent } from '../job-overview/dialogs/offset-channel-dialog/offset-channel-dialog.component';
import { CalibrationDialogComponent } from '../job-overview/dialogs/calibration-dialog/calibration-dialog.component';
import { EditUserChannelsDialogComponent } from '../job-overview/dialogs/edit-user-channels-dialog/edit-user-channels-dialog.component';

// Enums
import { StatusType } from '../../../../shared/enums';
import { Roles } from '../../../auth/models/roles.enum';
import { ChangeType } from '../../enums/change-type.enum';

// Hub
import { JobHub } from '../../services/job.hub';

// Pipes
import { MilitaryTimePipe, MilitaryTimeDatePipe } from '../../../../shared/pipes';

// Store
import { DashboardState } from '../dashboard/state/dashboard.state';

// Helpers
import { UtilityHelper } from '../../../../shared/helpers';

@Component({
	selector: 'job-channel-display',
	templateUrl: './job-channel-display.component.html',
	styleUrls: ['job-channel-display.component.scss'],
	providers: [MilitaryTimePipe, MilitaryTimeDatePipe],
})
export class JobChannelDisplayComponent extends AppCleanup implements OnInit, OnChanges, OnDestroy {
	@HostBinding('class') classes = 'component-container';
	@Select(DashboardState.currentDashboardGridsterItems) currentDashboardGridsterItems$: Observable<GridsterItem[]>;
	currentDashboardGridsterItems: GridsterItem[];

	@Input() gridsterItem: GridsterItem; /** The gridster item information of the Job Chart. Will be null if not in Dashboard*/
	@Input() jobKey: string; /** Job Key */
	@Input() isEditable: boolean = false; /** Widget is in an edit state */

	/** Triggers event to indicate widget has closed */
	@Output() onDelete: EventEmitter<boolean> = new EventEmitter();
	@Output() onCopy: EventEmitter<any> = new EventEmitter();
	job: Job;
	isWidget = false;
	vertical = false; /** Is the JobChart component taller than it is wide */
	initializingData: boolean = true;
	jobInitialized = false;
	isPublic: boolean; /**If true, the Job is viewable to the Public through a snapshot key. If false, you must be logged in to see the job*/
	userJobChannelValues: UserUnitPeriodChannelValue[];
	userJobChannelValuesToShow: UserUnitPeriodChannelValue[] = [];
	isEditing = false;
	channelClass: string = '';
	lastDataPerUnitPeriod: JobData[] = [];
	jobId: number;
	isAdmin: boolean;
	bufferedLiveData: JobData[] = [];
	chartState: ChartState;
	unitPeriodError: boolean = false;
	isEditMode: boolean = false;

	constructor(
		public dashboardService: DashboardService,
		private authService: AuthService,
		private subheaderService: SubheaderService,
		private timezoneService: TimezoneService,
		private dialog: MatDialog,
		public jobService: JobService,
		private coreService: CoreService,
		private jobHub: JobHub,
		public route: ActivatedRoute,
		private dcgService: DCGService,
		private jobOverviewService: JobOverviewService,
		private spinnerService: SpinnerService
	) {
		super();
	}

	ngOnInit() {
		this.initializeComponents();

		this.jobService.newDataSent$
			.pipe(share())
			.pipe(takeUntil(this.destroy))
			.subscribe((jobData) => {
				if (jobData && jobData.length > 0 && (jobData[0].k === this.jobKey || (this.jobId && jobData[0].k === String(this.jobId)))) {
					this.bufferedLiveData.push(...jobData);
				}
			});

		this.dashboardService.gridsterItemUpdated$.pipe(takeUntil(this.destroy)).subscribe((gridsterItem) => {
			if (gridsterItem && this.gridsterItem && gridsterItem.chartName == this.gridsterItem.chartName) {
				this.gridsterItem = gridsterItem;
			}
		});

		this.jobOverviewService.chartState$.pipe(takeUntil(this.destroy)).subscribe((chartState) => {
			this.chartState = chartState;
		});

		this.currentDashboardGridsterItems$.pipe(takeUntil(this.destroy)).subscribe((currentDashboardGridsterItems) => {
			if (currentDashboardGridsterItems) {
				this.currentDashboardGridsterItems = UtilityHelper.deepCopy(currentDashboardGridsterItems);
			}
		});

		this.dashboardService.gridsterConfig$.pipe(takeUntil(this.destroy)).subscribe((gridsterConfig) => {
			if (gridsterConfig) {
				this.isEditMode = gridsterConfig?.draggable?.enabled;
			}
		});
	}

	ngOnChanges() {
		this.vertical = this.gridsterItem.isVertical;
		if (this.gridsterItem) {
			this.isWidget = true;
		}
	}

	ngOnDestroy() {
		super.ngOnDestroy();
	}

	initializeComponents() {
		this.isPublic = this.route.snapshot.data && this.route.snapshot.data.hasOwnProperty('public') && this.route.snapshot.data.public;

		this.subheaderService.disabled$.next(false);

		this.authService
			.checkAccess([Roles.TenantAdmin, Roles.SuperAdmin, Roles.Admin], () => {
				return true;
			})
			.pipe(takeUntil(this.destroy))
			.subscribe((isAdmin) => {
				this.isAdmin = isAdmin;
			});

		this.jobKey = this.jobKey || this.route.snapshot.paramMap.get('jobId');

		if (!this.isPublic) {
			this.jobHub.connectionEstablished$.pipe(takeUntil(this.destroy)).subscribe((result) => {
				if (result) {
					this.jobHub.startSendingData([this.jobKey]);
				}
			});

			this.initializeJob();
			this.setIntervalForLiveData();
		} else {
			this.jobService
				.getJobIdFromKey(this.jobKey)
				.pipe(takeUntil(this.destroy))
				.subscribe((jobId) => {
					if (jobId) {
						this.jobId = jobId;

						//Needed for public links since Parent does not subscribe
						//this.jobHub.startSendingData([String(this.jobId)]);
						//this.jobHub.registerOnServerEvents();

						this.jobHub.connectionEstablished$.pipe(takeUntil(this.destroy)).subscribe((result) => {
							if (result) {
								this.jobHub.startSendingData([String(this.jobId)]);
							}
						});

						this.initializeJob();
						this.setIntervalForLiveData();
					}
				});
		}
	}

	setIntervalForLiveData(interval: number = 1000) {
		let lastDeepCopyOfLastJobData: JobData[] = [];

		setInterval(() => {
			if (lastDeepCopyOfLastJobData.length === 0 || lastDeepCopyOfLastJobData.length !== this.lastDataPerUnitPeriod.length) {
				lastDeepCopyOfLastJobData = UtilityHelper.deepCopy(this.lastDataPerUnitPeriod);
			}

			lastDeepCopyOfLastJobData.forEach((jobData) => {
				let lastJobDataForUnitPeriod = this.lastDataPerUnitPeriod.find((ljd) => ljd.u === jobData.u);

				if (lastJobDataForUnitPeriod && jobData.d < lastJobDataForUnitPeriod.d) {
					let deepCopyOfLastJobData = UtilityHelper.deepCopy(lastJobDataForUnitPeriod);

					if (deepCopyOfLastJobData) {
						deepCopyOfLastJobData.c.forEach((c) => {
							let unitPeriodId = this.userJobChannelValuesToShow.find((ujc) => ujc.userUnitPeriodChannel.unitPeriodChannel.unitPeriodId === deepCopyOfLastJobData.u)?.userUnitPeriodChannel?.unitPeriodChannel?.unitPeriodId;
							let findIndex = this.userJobChannelValuesToShow.findIndex((ujc) => ujc.userUnitPeriodChannel.unitPeriodChannel.unitPeriodId === unitPeriodId && ujc.userUnitPeriodChannel.unitPeriodChannel.channelId === c.i);
							if (findIndex >= 0) {
								this.userJobChannelValuesToShow[findIndex].value = c.v;
							}
						});
					}

					let findIndex = lastDeepCopyOfLastJobData.findIndex((ldc) => ldc.u === jobData.u);

					if (findIndex >= 0) {
						lastDeepCopyOfLastJobData[findIndex] = deepCopyOfLastJobData;
					}
				}
			});

			if (this.bufferedLiveData.length > 0) {
				// transform the data
				let newData = this.jobService.applyOffsetsToDataPoints(this.jobKey, this.bufferedLiveData);
				newData = this.jobService.applyPrecisionToDataPoints(this.jobKey, newData);

				// update most recent unit period data
				this.lastDataPerUnitPeriod = this.updateDataByUnitPeriodId(this.lastDataPerUnitPeriod, newData);

				newData = this.jobService.filterOutOfRangePoints(this.jobKey, newData);
			}

			this.bufferedLiveData = [];
		}, interval);
	}

	updateDataByUnitPeriodId(currentData: JobData[], newData: Array<JobData>): JobData[] {
		const updatedData = [...currentData];

		newData.forEach((newItem) => {
			let existingItemIndex = updatedData.findIndex((existingItem) => existingItem.u === newItem.u);

			if (existingItemIndex < 0) {
				updatedData.push(newItem);
			} else {
				updatedData[existingItemIndex] = newItem;
			}
		});

		return updatedData;
	}

	/**Initializes all Job Data and fires the jobChange$ observable. This initializes the Job data for all child components as well.
	 * If the Job is ready or has changed, the jobChange$ observable will refresh the chart with a new API call.
	 */
	initializeJob() {
		// Make a very basic call to the Job Table
		if (this.isPublic && !this.jobInitialized) {
			this.jobService.initPublicJob(this.jobKey);
		}

		// NEW: Use job-specific stream instead of global stream with filtering
		this.jobService
			.getJobStream(this.jobKey)
			.pipe(takeUntil(this.destroy), debounceTime(100))
			.subscribe((jobUpdate: JobUpdate) => {
				if (jobUpdate) {
					// Because I don't know why
					if (jobUpdate.changeType === ChangeType.INITIAL && this.isPublic && this.jobInitialized) return;

					if (!this.jobInitialized) {
						this.jobInitialized = true;
						this.job = jobUpdate.job;
						this.mergeChannels();
						this.initializingData = false;
					} else if (jobUpdate.changeType === ChangeType.SYNC_CHANGE) {
						this.job.lastSyncRequest = jobUpdate.job.lastSyncRequest;
					} else {
						this.job = this.jobService.findJob(jobUpdate.job.key);

						this.mergeChannels();

						let userUnitPeriodChannels = this.findUserUnitPeriodChannels();

						if (jobUpdate.changeType === ChangeType.USER_UNIT_PERIOD_CHANNEL) {
						}
					}

					if (this.job && this.job.unitPeriods.length === 0) {
						this.initializingData = false;
						this.unitPeriodError = true;
					}
				}
			});

		// LEGACY: Keep fallback for backward compatibility during migration
		// this.jobService.jobChange$.pipe(takeUntil(this.destroy), debounceTime(100)).subscribe((jobChange) => {
		// 	if (jobChange && (jobChange.job.key == this.jobKey || this.isPublic) && !this.jobInitialized) {
		// 		// Only fallback if job-specific stream hasn't initialized yet
		// 		if (jobChange.changeType === ChangeType.INITIAL && this.isPublic && this.jobInitialized) return;

		// 		if (!this.jobInitialized) {
		// 			this.jobInitialized = true;
		// 			this.job = jobChange.job;
		// 			this.mergeChannels();
		// 			this.initializingData = false;
		// 		}
		// 	}
		// });
	}

	displayTimeZone(date: Date): Date {
		return this.timezoneService.timeZoneDate(date, this.job?.utcDateTimeOffset || this.timezoneService.getCurrentTimeZone());
	}

	/**
	 * For public job's, the User Job Channels will be the same as Job Channels since there is no concept of a User.
	 * This method also determines which Channels to show in the Channel Display based on the User Job Channels.
	 */
	mergeChannels() {
		let tempCopyOfChannelValues = UtilityHelper.deepCopy(this.userJobChannelValues);
		this.userJobChannelValues = [];

		this.findUserUnitPeriodChannels().forEach((uupc) => {
			// Getting currentValue of channel so that channel display does not blank out on Job Change
			let currentValue = null;

			if (tempCopyOfChannelValues?.length > 0) {
				currentValue = tempCopyOfChannelValues.find((tc) => tc.userUnitPeriodChannel?.id === uupc.id)?.value;
			}

			let userJobChannelValue: UserUnitPeriodChannelValue = {
				userUnitPeriodChannel: uupc,
				value: currentValue,
			};
			this.userJobChannelValues.push(userJobChannelValue);
		});

		this.userJobChannelValues.sort((a, b) => {
			return a.userUnitPeriodChannel.seq - b.userUnitPeriodChannel.seq;
		});

		this.userJobChannelValuesToShow = this.userJobChannelValues.filter((userJobChannelValue) => userJobChannelValue.userUnitPeriodChannel.display).slice(0, 12);
		this.getChannelClass();
	}

	/**
	 * Highlights, or un-Highlights a series line. This is used on the UI when a Channel Display is hovered, causing the Channel Chart series to highlight (become bolder)
	 * @param userJobChannelValue User Job Channel that is being hovered in the display
	 * @param highlight Whether to highlight, or un-highlight the series
	 */
	highlightSeries(userJobChannelValue: UserUnitPeriodChannelValue, highlight: boolean) {
		if (this.chartState && !this.isWidget) {
			let channel = userJobChannelValue.userUnitPeriodChannel;
			if (Object.prototype.hasOwnProperty.call(this.chartState.series, `${channel.unitPeriodChannel.unitPeriodId}_${channel.unitPeriodChannel.channelId}`)) {
				this.chartState.series[`${channel.unitPeriodChannel.unitPeriodId}_${channel.unitPeriodChannel.channelId}`].strokeWidth = highlight ? channel.lineWeight * 2 : channel.lineWeight;
				if (this.vertical) {
					this.chartState.series[`${channel.unitPeriodChannel.unitPeriodId}_${channel.unitPeriodChannel.channelId}`].xAxis.renderer.line.strokeWidth = highlight ? 3 : 1;
					this.chartState.series[`${channel.unitPeriodChannel.unitPeriodId}_${channel.unitPeriodChannel.channelId}`].xAxis.renderer.fontWeight = highlight ? 'bold' : 'normal';
				} else {
					this.chartState.series[`${channel.unitPeriodChannel.unitPeriodId}_${channel.unitPeriodChannel.channelId}`].yAxis.renderer.line.strokeWidth = highlight ? 3 : 1;
					this.chartState.series[`${channel.unitPeriodChannel.unitPeriodId}_${channel.unitPeriodChannel.channelId}`].yAxis.renderer.fontWeight = highlight ? 'bold' : 'normal';
				}
			}
		}
	}

	openOffsetChannelDialog(channelValue: UserUnitPeriodChannelValue) {
		this.dialog.open(OffsetChannelDialogComponent, {
			data: {
				userJobChannelValue: channelValue,
				key: this.jobKey,
			},
			width: '100%',
			maxWidth: '400px',
			autoFocus: false,
		});
	}

	openCalibrationDialog(channelValue: UserUnitPeriodChannelValue) {
		const dialogRef = this.dialog.open(CalibrationDialogComponent, {
			data: {
				userJobChannelValue: channelValue,
				key: this.jobKey,
			},
			width: '100%',
			maxWidth: '600px',
			autoFocus: false,
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe((dto: CalibrationCloseDto) => {
				if (!dto) return;
				this.dcgService
					.forceRestartUnit(dto.unitGuid)
					.pipe(takeUntil(this.destroy))
					.subscribe({
						next: async () => {
							const message = 'Unit successfully re-started';
							this.coreService.toast({ message: message, type: StatusType.SUCCESS });
						},
						error: () => {
							const message = 'Unit failed to re-start, please contact support for assistance';
							this.coreService.toast({ message: message, type: StatusType.DANGER });
						},
					});
			});
	}

	editUserChannelSettings(chartState: ChartState) {
		if (this.isPublic) {
			return;
		}
		const channels: UserUnitPeriodChannelDto[] = UtilityHelper.deepCopy(this.findUserUnitPeriodChannels());
		const dialogRef = this.dialog.open(EditUserChannelsDialogComponent, {
			data: {
				key: this.jobKey,
				userUnitPeriodChannels: channels,
				isWidget: this.isWidget,
				jobChartWidget: false,
			},
			width: this.isWidget ? null : '100%',
			maxWidth: '1200px',
			autoFocus: false,
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe((data: { key: string; userJobChannels: UserUnitPeriodChannelDto[] }) => {
				if (data) {
					if (!this.gridsterItem) {
						let i = 0;

						data.userJobChannels.forEach((ujc) => {
							ujc.seq = i;
							i++;
						});

						this.spinnerService.showAppSpinner(); // Turns on app wide spinner
						forkJoin([this.jobService.putUserJobChannelSequences(data.key, data.userJobChannels), this.jobService.updateUserJobChannels(data.key, data.userJobChannels)])
							.pipe(takeUntil(this.destroy))
							.subscribe((result: any) => {
								this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
							});

						this.mergeChannels();
					} else {
						this.gridsterItem.additionalJsonSettings = JSON.stringify(data.userJobChannels);

						this.mergeChannels();

						let dashboardItem = this.currentDashboardGridsterItems?.find((di) => di.chartName === this.gridsterItem.chartName);

						if (dashboardItem) dashboardItem.additionalJsonSettings = this.gridsterItem.additionalJsonSettings;

						this.dashboardService.dashboardSaveDisplay(true);
					}
				}
			});
	}

	deleteWidget() {
		this.onDelete.emit(true);
	}

	copyWidget() {
		this.onCopy.emit();
	}

	/* Determines the class to apply to the Channel Display values, depending on the number of User Job Channels are being shown*/
	getChannelClass() {
		var count = 0;

		if (!this.isEditing) {
			count = this.userJobChannelValuesToShow.length || this.findUserUnitPeriodChannels().filter((c) => c.display).length;
		} else {
			count = this.findUserUnitPeriodChannels().length;
		}

		if (count > 15) {
			this.channelClass = 'row-cols-2 row-cols-sm-4 row-cols-md-4 row-cols-lg-6 row-cols-xl-6';
		}
		if (count == 15) {
			this.channelClass = 'row-cols-2 row-cols-sm-4 row-cols-md-5 row-cols-lg-5 row-cols-xl-5';
		}
		if (count == 14) {
			this.channelClass = 'row-cols-2 row-cols-sm-4 row-cols-md-5 row-cols-lg-5 row-cols-xl-5';
		}
		if (count == 13) {
			this.channelClass = 'row-cols-2 row-cols-sm-4 row-cols-md-4 row-cols-lg-4 row-cols-xl-4';
		}
		if (count == 12) {
			this.channelClass = 'row-cols-2 row-cols-sm-4 row-cols-md-4 row-cols-lg-6 row-cols-xl-6';
		}
		if (count == 11) {
			this.channelClass = 'row-cols-2 row-cols-sm-4 row-cols-md-4 row-cols-lg-6 row-cols-xl-6';
		}
		if (count == 10) {
			this.channelClass = 'row-cols-2 row-cols-sm-4 row-cols-md-5 row-cols-lg-5 row-cols-xl-5';
		}
		if (count == 9) {
			this.channelClass = 'row-cols-2 row-cols-sm-3 row-cols-md-5 row-cols-lg-5 row-cols-xl-5';
		}
		if (count == 8) {
			this.channelClass = 'row-cols-2 row-cols-sm-4 row-cols-md-4 row-cols-lg-4 row-cols-xl-4';
		}
		if (count == 7) {
			this.channelClass = 'row-cols-2 row-cols-sm-4 row-cols-md-4 row-cols-lg-4 row-cols-xl-4';
		}
		if (count == 6) {
			this.channelClass = 'row-cols-2 row-cols-sm-3 row-cols-md-3 row-cols-lg-6 row-cols-xl-6';
		}
		if (count == 5) {
			this.channelClass = 'row-cols-2 row-cols-sm-3 row-cols-md-3 row-cols-lg-5 row-cols-xl-5';
		}
		if (count == 4) {
			this.channelClass = 'row-cols-2 row-cols-sm-4 row-cols-md-4 row-cols-lg-4 row-cols-xl-4';
		}
		if (count == 3) {
			this.channelClass = 'row-cols-2 row-cols-sm-3 row-cols-md-3 row-cols-lg-4 row-cols-xl-4';
		}
		if (count == 2) {
			this.channelClass = 'row-cols-2 row-cols-sm-3 row-cols-md-3 row-cols-lg-4 row-cols-xl-4';
		}
		if (count == 1) {
			this.channelClass = 'row-cols-2 row-cols-sm-3 row-cols-md-3 row-cols-lg-4 row-cols-xl-4';
		}
		if (count == 0) {
			this.channelClass = '';
		}
	}

	findUserUnitPeriodChannels(): UserUnitPeriodChannelDto[] {
		if (!this.gridsterItem?.additionalJsonSettings) return this.jobService.findUserUnitPeriodChannelsForJob(this.job?.key);
		return JSON.parse(this.gridsterItem.additionalJsonSettings);
	}
}
